"use client";

import { useEffect, useRef } from "react";
import LoadingState from "@/components/LoadingState";

export default function InfiniteScrollSentinel({
  enabled,
  loading,
  onLoadMore,
  label = "Loading more posts",
  preloadDistance = 320,
}) {
  const sentinelRef = useRef(null);
  const loadingRef = useRef(loading);
  const onLoadMoreRef = useRef(onLoadMore);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!enabled || !node) return undefined;

    let armed = true;
    const update = (nearViewport) => {
      if (!nearViewport) {
        armed = true;
        return;
      }
      if (!armed || loadingRef.current) return;
      armed = false;
      onLoadMoreRef.current?.();
    };

    if (typeof IntersectionObserver !== "undefined") {
      const observer = new IntersectionObserver(
        ([entry]) => update(entry.isIntersecting),
        { rootMargin: `${preloadDistance}px 0px` }
      );
      observer.observe(node);
      return () => observer.disconnect();
    }

    let frame = 0;
    const checkPosition = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = node.getBoundingClientRect();
        update(
          rect.top <= window.innerHeight + preloadDistance &&
            rect.bottom >= -preloadDistance
        );
      });
    };
    window.addEventListener("scroll", checkPosition, { passive: true });
    window.addEventListener("resize", checkPosition);
    checkPosition();
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", checkPosition);
      window.removeEventListener("resize", checkPosition);
    };
  }, [enabled, preloadDistance]);

  if (!enabled) return null;

  return (
    <div
      ref={sentinelRef}
      className={loading ? "flex min-h-12 items-center justify-center py-2" : "h-px"}
      data-feed-end-sentinel=""
    >
      {loading ? (
        <LoadingState
          variant="inline"
          message={label}
          showMessage={false}
        />
      ) : null}
    </div>
  );
}
